import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import { useNotification } from "@context/NotificationContext";
import {
  useVisitorDetailsReferenceData,
  type CompanyType,
} from "@features/gatepass/hooks/useVisitorDetailsReferenceData";
import { isDateOnOrAfterToday } from "@features/gatepass/schema/gatepassScValidity.utils";
import { type NormalGatepassFormData } from "@features/gatepass/schema/normalGatepassFormSchema";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { type Gates } from "@services/reference-data/types";
import { resolveGatepassRegistrationEmiratePrefill } from "@services/referenceDataApi";
import {
  checkGatepassSecurityClearance,
  getMySecurityClearance,
  getSecurityClearanceById,
} from "@services/securityClearanceApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic.ts";
import { t } from "i18next";
import { Controller, FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useNormalGatepassFormStore } from "../../store/useNormalGatepassForm.store";
import {
  createScIdValidators,
  extractScErrorDetail,
  formatDateToDdMmYyyy,
  formatPortOptions,
  requiresManualSecurityClearanceCheck,
  resolveScSearchField,
} from "../utils";
import SecurityClearanceErrorMessage from "./SecurityClearanceErrorMessage";
import SecurityClearanceStatusMessage from "./SecurityClearanceStatusMessage";

export interface FormContext {
  formData: UseFormReturn<NormalGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}

const TRANSLATION_KEY = "gatepass";
const gatepassService = "bff-gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];
const SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Normal Gatepass Visitor Details",
  action: "check_security_clearance",
  entityType: "security_clearance",
};
const SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT = {
  module: "gatepass",
  screen: "Normal Gatepass Visitor Details",
  action: "populate_security_clearance_from_document_id",
  fromField: "passportNumber_or_emiratesIdNumber",
  toField: "securityClearance",
  entityType: "security_clearance",
};

const VisitorDetails = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { ports, isLoadingPorts, gates, isLoadingGates, companies, fottDisplayOptions } =
    useVisitorDetailsReferenceData({ gatepassService });

  // Security clearance validation state
  const { notify } = useNotification();
  const duplicateScMessage = t(`${TRANSLATION_KEY}.notifications.group-member-already-added`);
  const [isCheckingPassport, setIsCheckingPassport] = useState(false);
  const [lastSearchField, setLastSearchField] = useState<"passport" | "emirates" | null>(null);
  const [scCheckErrorMessage, setScCheckErrorMessage] = useState("");
  const { setField } = useNormalGatepassFormStore();
  const syncScFields = useCallback(
    (
      values: Partial<
        Pick<NormalGatepassFormData, "SCStatus" | "securityClearanceSerial" | "securityClearanceId">
      >,
      options?: { shouldValidate?: boolean },
    ) => {
      const shouldValidate = options?.shouldValidate ?? true;

      if (values.SCStatus !== undefined) {
        formData.setValue("SCStatus", values.SCStatus, { shouldDirty: true, shouldValidate });
        setField("SCStatus", values.SCStatus);
        updateFormData("SCStatus", values.SCStatus);
      }

      if (values.securityClearanceSerial !== undefined) {
        formData.setValue("securityClearanceSerial", values.securityClearanceSerial, {
          shouldDirty: true,
          shouldValidate,
        });
        setField("securityClearanceSerial", values.securityClearanceSerial);
        updateFormData("securityClearanceSerial", values.securityClearanceSerial);
      }

      if (values.securityClearanceId !== undefined) {
        formData.setValue("securityClearanceId", values.securityClearanceId, {
          shouldDirty: true,
          shouldValidate,
        });
        setField("securityClearanceId", values.securityClearanceId);
        updateFormData("securityClearanceId", values.securityClearanceId);
      }
    },
    [formData, setField, updateFormData],
  );
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const bootstrapSecurityClearanceId = useAuthStore((state) => state.bootstrapSecurityClearanceId);
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => userRoles.includes(role));
  const requiresManualScCheck = requiresManualSecurityClearanceCheck(userRoles);

  const { data: latestApprovedScId, isPending: isLoadingLatestApprovedSc } = useQuery({
    queryKey: ["gatepass-individual-my-sc", userId],
    queryFn: async () => {
      try {
        const me = await getMySecurityClearance({ service: gatepassService });
        const current = me as typeof me & {
          securityClearanceId?: string;
          securityClearanceRequestId?: string;
          securityClearanceStatus?: string;
        };
        const status = String(
          current.status ?? current.securityClearanceStatus ?? "",
        ).toUpperCase();
        if (status !== "APPROVED") return null;
        const id = String(
          current.securityClearanceId ??
            current.securityClearanceRequestId ??
            current.id ??
            me?.id ??
            "",
        ).trim();
        if (!id) return null;
        return id;
      } catch {
        return null;
      }
    },
    enabled: isIndividualRole && Boolean(userId),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Debounced passport checking
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const { isPassportValid, isEmiratesIdValid } = useMemo(
    () => createScIdValidators(TRANSLATION_KEY, t),
    [],
  );
  const previousPassportRef = useRef("");
  const previousEmiratesIdRef = useRef("");

  const syncTextField = useCallback(
    (
      field: keyof NormalGatepassFormData,
      value: string,
      options?: { shouldValidate?: boolean },
    ) => {
      const shouldValidate = options?.shouldValidate ?? true;
      formData.setValue(field, value, {
        shouldDirty: true,
        shouldValidate,
      });
      setField(field, value);
      updateFormData(field, value);
    },
    [formData, setField, updateFormData],
  );

  function syncBooleanField(
    field: "FOTTEntry" | "isFottDisabled",
    value: boolean,
    options?: { shouldValidate?: boolean },
  ) {
    formData.setValue(field, value, {
      shouldDirty: true,
      shouldValidate: options?.shouldValidate ?? true,
    });
    setField(field, value);
    updateFormData(field, value);
  }

  function clearScDerivedFields() {
    syncTextField("fullName", "", { shouldValidate: false });
    syncTextField("nationality", "", { shouldValidate: false });
    syncTextField("passportNumberExpirationDate", "", { shouldValidate: false });
    syncTextField("phoneNumber", "", { shouldValidate: false });
    syncTextField("sponsorCompany", "", { shouldValidate: false });
    syncTextField("vehicleType", "", { shouldValidate: false });
    syncTextField("registrationEmirate", "", { shouldValidate: false });
    syncTextField("plateNumber", "", { shouldValidate: false });
    syncTextField("securityClearanceValidity", "", { shouldValidate: false });
    syncTextField("securityClearanceFott", "", { shouldValidate: false });
    syncBooleanField("FOTTEntry", true, { shouldValidate: false });
    syncBooleanField("isFottDisabled", false, { shouldValidate: false });
    syncTextField("visitorCompany", "", { shouldValidate: false });
    syncTextField("visitorCompanyArabic", "", { shouldValidate: false });
  }

  const checkPassportMutation = useMutation({
    mutationFn: async ({
      passportNumber,
      emiratesIdNumber,
      trackInquiry,
    }: {
      passportNumber?: string;
      emiratesIdNumber?: string;
      trackInquiry?: boolean;
    }) => {
      const hasPassport = Boolean(passportNumber?.trim());
      const hasEmiratesId = Boolean(emiratesIdNumber?.trim());

      if (!hasPassport && !hasEmiratesId) {
        return null;
      }

      const execute = () =>
        checkGatepassSecurityClearance({
          service: gatepassService,
          ...(hasPassport ? { passportNumber } : {}),
          ...(hasEmiratesId ? { emiratesId: emiratesIdNumber } : {}),
        });

      if (trackInquiry) {
        return runFieldNavigation(SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT, () =>
          runInquiry(SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT, execute),
        );
      }

      return execute();
    },
    onSuccess: (response, variables) => {
      setScCheckErrorMessage("");
      const passportNumber = variables.passportNumber?.trim();
      const emiratesIdNumber = variables.emiratesIdNumber?.trim();
      setLastSearchField(resolveScSearchField(passportNumber, emiratesIdNumber));

      const hasScRecord = Boolean(response?.status || response?.securityClearanceId);
      const statusUpper = String(response?.status ?? "").toUpperCase();
      const scValidFlagProvided = typeof response?.valid === "boolean";
      const isApproved = statusUpper === "APPROVED";
      const expiryDateRaw = response?.expiryDate ?? response?.expirationDate;
      const isNotExpired =
        typeof expiryDateRaw === "string" && expiryDateRaw.trim()
          ? isDateOnOrAfterToday(expiryDateRaw)
          : true;
      const isEligible =
        hasScRecord &&
        (scValidFlagProvided ? response?.valid === true : isApproved && isNotExpired);
      if (!response || !isEligible) {
        syncScFields({
          SCStatus: "NOT_FOUND",
          securityClearanceSerial: "",
          securityClearanceId: "",
        });
        syncTextField("securityClearanceValidity", "", { shouldValidate: false });
        syncTextField("securityClearanceFott", "", { shouldValidate: false });
        setIsCheckingPassport(false);
        return;
      }

      const foundSecurityClearanceId = String(response.securityClearanceId ?? "").trim();
      const responsePassportNumber = String(response.passportNumber ?? passportNumber ?? "").trim();
      const responseEmiratesIdNumber = String(
        response.emiratesIdNumber ?? emiratesIdNumber ?? "",
      ).trim();
      const currentGroupMembers = ((formData.getValues(
        "groupMembers",
      ) as NormalGatepassFormData["groupMembers"]) || []) as Array<{
        passportNumber?: string;
        emiratesIdNumber?: string;
        securityClearanceId?: string;
      }>;

      const isDuplicateWithGroupMember = currentGroupMembers.some((member) => {
        const memberScId = String(member?.securityClearanceId ?? "").trim();
        const memberPassport = String(member?.passportNumber ?? "").trim();
        const memberEmiratesId = String(member?.emiratesIdNumber ?? "").trim();

        if (foundSecurityClearanceId && memberScId) {
          return memberScId === foundSecurityClearanceId;
        }

        return Boolean(
          (responsePassportNumber && memberPassport && memberPassport === responsePassportNumber) ||
            (responseEmiratesIdNumber &&
              memberEmiratesId &&
              memberEmiratesId === responseEmiratesIdNumber),
        );
      });

      if (isDuplicateWithGroupMember) {
        syncScFields(
          { SCStatus: "", securityClearanceSerial: "", securityClearanceId: "" },
          { shouldValidate: false },
        );
        formData.setError("securityClearanceId", {
          type: "manual",
          message: duplicateScMessage,
        });
        setIsCheckingPassport(false);
        return;
      }

      syncScFields({
        SCStatus: statusUpper || "NOT_FOUND",
        securityClearanceSerial: String(response.serialNo ?? response.serialNumber ?? ""),
        securityClearanceId: foundSecurityClearanceId,
      });
      syncTextField("securityClearanceValidity", formatDateToDdMmYyyy(response.expiryDate));
      const fottFromSc = response.fott === true ? t("yes") : t("no");
      syncTextField("securityClearanceFott", fottFromSc);
      formData.clearErrors(["passportNumber", "emiratesIdNumber", "securityClearanceId"]);

      if (!isIndividualRole) {
        if (
          passportNumber &&
          responseEmiratesIdNumber &&
          !formData.getValues("emiratesIdNumber")?.trim()
        ) {
          syncTextField("emiratesIdNumber", responseEmiratesIdNumber);
        }

        if (
          emiratesIdNumber &&
          responsePassportNumber &&
          !formData.getValues("passportNumber")?.trim()
        ) {
          syncTextField("passportNumber", responsePassportNumber);
        }
      }

      const responseFullName = String(response.fullNameEnglish ?? "").trim();
      if (!formData.getValues("fullName") && responseFullName) {
        syncTextField("fullName", responseFullName);
      }

      const responseNationality = String(response.presentNationality ?? "").trim();
      if (responseNationality) {
        syncTextField("nationality", responseNationality);
      }

      const passportExpiryValue = formatDateToDdMmYyyy(response.passportExpiryDate);
      syncTextField("passportNumberExpirationDate", passportExpiryValue);

      const phoneNumberValue = response.mobileNumber ?? "";
      syncTextField("phoneNumber", String(phoneNumberValue).trim());

      const sponsorCompanyValue = String(response.requestingSponsorEnglish ?? "").trim();
      if (sponsorCompanyValue) {
        syncTextField("sponsorCompany", sponsorCompanyValue);
      }

      const vehicleType = String(response.vehicleDetails?.type ?? "").trim();
      if (vehicleType) {
        syncTextField("vehicleType", vehicleType);
      }

      syncTextField(
        "registrationEmirate",
        resolveGatepassRegistrationEmiratePrefill(response.vehicleDetails?.placeOfIssue),
      );

      const vehiclePlateNumber = String(response.vehicleDetails?.plateNo ?? "").trim();
      if (vehiclePlateNumber) {
        syncTextField("plateNumber", vehiclePlateNumber);
      }

      const visitorCompanyEnglish = String(response.visitorCompanyNameEnglish ?? "").trim();
      if (visitorCompanyEnglish) {
        syncTextField("visitorCompany", visitorCompanyEnglish);
      }

      const visitorCompanyArabic = String(response.visitorCompanyNameArabic ?? "").trim();
      if (visitorCompanyArabic) {
        syncTextField("visitorCompanyArabic", visitorCompanyArabic);
      }

      const entryPurposeEnglish = String(response.entryPurposeEnglish ?? "").trim();
      if (entryPurposeEnglish) {
        const currentLocations = formData.getValues("locations") ?? [];
        const updatedLocations =
          currentLocations.length > 0
            ? currentLocations.map((location, index) =>
                index === 0 ? { ...location, entryPurpose: entryPurposeEnglish } : location,
              )
            : [{ location: "", entryPurpose: entryPurposeEnglish }];

        formData.setValue("locations", updatedLocations, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setField("locations", updatedLocations);
        updateFormData("locations", updatedLocations);
      }

      if (response.fott !== undefined) {
        const fottValue = response.fott === true;

        formData.setValue("FOTTEntry", fottValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setField("FOTTEntry", fottValue);
        updateFormData("FOTTEntry", fottValue);

        setField("isFottDisabled", !fottValue);
        updateFormData("isFottDisabled", !fottValue);
      }

      setIsCheckingPassport(false);
    },
    onError: (error) => {
      const errorDetail = extractScErrorDetail(error);
      setScCheckErrorMessage(errorDetail);
      setLastSearchField(null);
      syncScFields({
        SCStatus: "",
        securityClearanceSerial: "",
        securityClearanceId: "",
      });
      syncTextField("securityClearanceValidity", "", { shouldValidate: false });
      syncTextField("securityClearanceFott", "", { shouldValidate: false });
      setIsCheckingPassport(false);
    },
  });

  // Individuals: load latest approved SC from the list (refreshes after approval without re-login)
  // and run gatepass SC validation so visitor fields are prefilled and bound to that clearance.

  useEffect(() => {
    if (!isIndividualRole || isLoadingLatestApprovedSc) return;
    const effectiveScId =
      (latestApprovedScId ?? "").trim() || (bootstrapSecurityClearanceId ?? "").trim();
    if (!effectiveScId) return;
    if (String(formData.getValues("securityClearanceId") ?? "").trim()) return;

    let cancelled = false;
    void (async () => {
      setIsCheckingPassport(true);
      try {
        const sc = await getSecurityClearanceById({
          id: effectiveScId,
          service: gatepassService,
        });
        if (cancelled) return;
        if (String(formData.getValues("securityClearanceId") ?? "").trim()) {
          setIsCheckingPassport(false);
          return;
        }
        const p = String(sc.applicant?.passportNumber ?? "").trim();
        const e = String(sc.applicant?.emiratesIdNumber ?? "").trim();
        if (!p && !e) {
          notify({
            title: t(`${TRANSLATION_KEY}.notifications.passport-check-error-title`),
            description: t(`${TRANSLATION_KEY}.notifications.passport-check-error-description`),
            type: "error",
          });
          setIsCheckingPassport(false);
          return;
        }
        syncTextField("passportNumber", p, { shouldValidate: false });
        syncTextField("emiratesIdNumber", e, { shouldValidate: false });
        previousPassportRef.current = p;
        previousEmiratesIdRef.current = e;
        checkPassportMutation.reset();
        checkPassportMutation.mutate({
          ...(p ? { passportNumber: p } : {}),
          ...(e ? { emiratesIdNumber: e } : {}),
        });
      } catch {
        if (!cancelled) {
          notify({
            title: t(`${TRANSLATION_KEY}.notifications.passport-check-error-title`),
            description: t(`${TRANSLATION_KEY}.notifications.passport-check-error-description`),
            type: "error",
          });
          setIsCheckingPassport(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    checkPassportMutation,
    isIndividualRole,
    isLoadingLatestApprovedSc,
    latestApprovedScId,
    notify,
    syncTextField,
  ]);

  const resetScLookupState = useCallback(() => {
    setLastSearchField(null);
    setScCheckErrorMessage("");
    syncScFields(
      { SCStatus: "", securityClearanceSerial: "", securityClearanceId: "" },
      { shouldValidate: false },
    );
  }, [syncScFields]);

  // Debounced ID checking
  const handleIdChange = useCallback(
    (passportValue: string, emiratesIdValue: string, preferredField?: "passport" | "emirates") => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        const passportNumber = passportValue?.trim() ?? "";
        const emiratesIdNumber = emiratesIdValue?.trim() ?? "";
        const hasValidPassport = isPassportValid(passportNumber);
        const hasValidEmiratesId = isEmiratesIdValid(emiratesIdNumber);

        const shouldCheckPassport =
          preferredField === "emirates"
            ? !hasValidEmiratesId && hasValidPassport
            : hasValidPassport;

        const shouldCheckEmiratesId =
          preferredField === "emirates"
            ? hasValidEmiratesId
            : !hasValidPassport && hasValidEmiratesId;

        if (shouldCheckPassport || shouldCheckEmiratesId) {
          setIsCheckingPassport(true);
          setScCheckErrorMessage("");
          checkPassportMutation.reset();
          checkPassportMutation.mutate({
            ...(shouldCheckPassport ? { passportNumber } : {}),
            ...(shouldCheckEmiratesId ? { emiratesIdNumber } : {}),
            trackInquiry: true,
          });
        } else {
          resetScLookupState();
        }
      }, 500);

      setDebounceTimer(timer);
    },
    [debounceTimer, checkPassportMutation, isEmiratesIdValid, isPassportValid, resetScLookupState],
  );

  const clearFieldValue = useCallback(
    (field: "passportNumber" | "emiratesIdNumber") => {
      formData.setValue(field, "", { shouldDirty: true, shouldValidate: true });
      updateFormData(field, "");
      if (field === "passportNumber") {
        previousPassportRef.current = "";
      } else {
        previousEmiratesIdRef.current = "";
      }
    },
    [formData, updateFormData],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const formattedPortOptions = useMemo(() => formatPortOptions(ports), [ports]);

  const sponsorCompanies = useMemo(
    () =>
      companies.map(({ companyNameEnglish, companyCode }: CompanyType) => ({
        label: companyNameEnglish,
        value: companyCode,
      })),
    [companies],
  );

  const portValue = formData.watch("port");
  const gateValue = formData.watch("gate");

  const selectedPort = ports.find(
    (port) => String(port.portCode).trim() === (portValue?.trim() ?? ""),
  );
  const selectedPortCode = selectedPort ? String(selectedPort.portCode).trim() : "";
  const sponsorGates = useMemo(() => {
    if (!selectedPortCode) {
      return [];
    }

    return gates
      .filter((gate) => String(gate.portCode).trim() === selectedPortCode)
      .map(({ gateDesc, gateNo }: Gates) => ({
        label: gateDesc,
        value: gateNo,
      }));
  }, [gates, selectedPortCode]);

  const allowedGateValues = useMemo(
    () => new Set(sponsorGates.map((g) => String(g.value))),
    [sponsorGates],
  );

  const passportValue = formData.watch("passportNumber");
  const emiratesIdValue = formData.watch("emiratesIdNumber");
  const hasValidPassport = isPassportValid(passportValue ?? "");
  const hasValidEmiratesId = isEmiratesIdValid(emiratesIdValue ?? "");
  const scStatus = formData.watch("SCStatus") ?? "";
  const securityClearanceSerial = formData.watch("securityClearanceSerial") ?? "";
  const securityClearanceId = formData.watch("securityClearanceId") ?? "";
  const visitorCompanyEnglish = formData.watch("visitorCompany");
  const hasIdsForCheck = hasValidPassport || hasValidEmiratesId;
  const canClickCheckSc = !isCheckingPassport && hasIdsForCheck;
  const isGateDisabled = !portValue?.trim() || !selectedPortCode;
  const hasIdErrors = Boolean(
    formData.formState.errors.passportNumber || formData.formState.errors.emiratesIdNumber,
  );
  const securityClearanceError = formData.formState.errors.securityClearanceId?.message;
  const showSecurityClearanceError =
    Boolean(securityClearanceError) &&
    !scCheckErrorMessage &&
    (securityClearanceError === duplicateScMessage ||
      (formData.formState.submitCount > 0 && !scStatus));
  const hasScLookupState = Boolean(
    lastSearchField ||
      scStatus.trim() ||
      securityClearanceSerial.trim() ||
      securityClearanceId.trim(),
  );

  const handleSerialClick = useCallback(() => {
    if (securityClearanceId) {
      const url = `/${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${securityClearanceId}`;
      window.open(url, "_blank");
    }
  }, [securityClearanceId]);

  const handleManualScCheck = useCallback(() => {
    handleIdChange(passportValue ?? "", emiratesIdValue ?? "");
  }, [emiratesIdValue, handleIdChange, passportValue]);

  // If Port is cleared/changed, ensure Gate selection stays compatible
  useEffect(() => {
    const currentGate = gateValue?.trim();
    if (!currentGate) {
      return;
    }

    if (isLoadingPorts || isLoadingGates) {
      return;
    }

    if (!portValue?.trim() || !allowedGateValues.has(currentGate)) {
      formData.setValue("gate", "", { shouldDirty: true, shouldValidate: true });
      updateFormData("gate", "");
    }
  }, [
    portValue,
    gateValue,
    allowedGateValues,
    formData,
    updateFormData,
    isLoadingPorts,
    isLoadingGates,
  ]);

  const visitorScValidityError = formData.formState.errors.securityClearanceValidity?.message;

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.visitor-details`)}
        headerError={visitorScValidityError ? String(visitorScValidityError) : undefined}
      >
        <FormItem<NormalGatepassFormData, "passportNumber">
          required={!emiratesIdValue?.trim()}
          name="passportNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.passport-number`)}
          testId="normalGatepass-passportNumber"
          disabled={isIndividualRole}
          onFieldChange={(key: string, value: unknown) => {
            const nextPassport = String(value ?? "").trim();
            const currentPassport = previousPassportRef.current;
            const hasChanged = nextPassport !== currentPassport;
            previousPassportRef.current = nextPassport;
            updateFormData(key, value);
            if (hasChanged && formData.getValues("emiratesIdNumber")?.trim()) {
              clearFieldValue("emiratesIdNumber");
            }
            if (hasChanged) {
              if (hasScLookupState) {
                clearScDerivedFields();
                resetScLookupState();
              }
              formData.clearErrors("securityClearanceId");
            }
          }}
          enforceUppercase
        />
        <FormItem<NormalGatepassFormData, "emiratesIdNumber">
          required={!passportValue?.trim()}
          name="emiratesIdNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.emirates-id-number`)}
          testId="normalGatepass-emiratesIdNumber"
          formatOnChange={formatOnChangeEmiratesIDNumber}
          disabled={isIndividualRole}
          onFieldChange={(key: string, value: unknown) => {
            const nextEmiratesId = String(value ?? "").trim();
            const currentEmiratesId = previousEmiratesIdRef.current;
            const hasChanged = nextEmiratesId !== currentEmiratesId;
            previousEmiratesIdRef.current = nextEmiratesId;
            updateFormData(key, value);
            if (hasChanged && formData.getValues("passportNumber")?.trim()) {
              clearFieldValue("passportNumber");
            }
            if (hasChanged) {
              if (hasScLookupState) {
                clearScDerivedFields();
                resetScLookupState();
              }
              formData.clearErrors("securityClearanceId");
            }
          }}
        />
        <div className={`col-span-2 ${hasIdErrors ? "" : "-mt-4"}`}>
          <SecurityClearanceStatusMessage
            isChecking={isCheckingPassport}
            status={scStatus}
            serial={securityClearanceSerial}
            errorMessage={scCheckErrorMessage}
            lastSearchField={lastSearchField}
            onSerialClick={handleSerialClick}
            translationKey={TRANSLATION_KEY}
          />
          {showSecurityClearanceError && (
            <SecurityClearanceErrorMessage message={String(securityClearanceError)} />
          )}
        </div>
        {requiresManualScCheck && (
          <div className="mb-4 col-span-2">
            <Button
              testId="normalGatepass-checkSc"
              type="button"
              variant="outlined"
              color="primary"
              className="bg-white"
              sx={{ textTransform: "none" }}
              disabled={!canClickCheckSc}
              onClick={handleManualScCheck}
              label={t(`${TRANSLATION_KEY}.check-security-clearance`)}
            />
          </div>
        )}
        <div className="col-span-2 flex flex-col gap-4 pb-7 md:flex-row">
          <div className="flex min-w-0 flex-1 flex-col">
            <CustomFormLabel sx={{ mb: 1 }} disabled>
              {t("gatepass.security-clearance-validity", {
                defaultValue: "Security clearance validity",
              })}
            </CustomFormLabel>
            <div className="mt-auto w-full">
              <Controller
                name="securityClearanceValidity"
                control={formData.control}
                render={({ field: inputField }) => (
                  <Input
                    {...inputField}
                    fullWidth
                    disabled
                    testId="normalGatepass-securityClearanceValidity"
                    placeholder="-"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <CustomFormLabel sx={{ mb: 1 }} disabled>
              {t("gatepass.fott", { defaultValue: "FOTT" })}
            </CustomFormLabel>
            <div className="mt-auto w-full">
              <Controller
                name="securityClearanceFott"
                control={formData.control}
                render={({ field: inputField }) => (
                  <RadioGroup
                    row
                    {...inputField}
                    value={inputField.value ?? ""}
                    onChange={(e) => inputField.onChange(e.target.value)}
                    sx={{
                      "display": "flex",
                      "flexDirection": "row",
                      "flexWrap": "wrap",
                      "columnGap": 2,
                      "rowGap": 0.5,
                      ".MuiFormControlLabel-root": {
                        marginRight: 0,
                      },
                    }}
                  >
                    {fottDisplayOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        disabled
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </div>
          </div>
        </div>
        <FormItem<NormalGatepassFormData, "fullName">
          required
          name="fullName"
          type="text"
          label={t(`${TRANSLATION_KEY}.fullName-en`)}
          testId="normalGatepass-fullName"
          disabled
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "nationality">
          required
          name="nationality"
          type="text"
          label={t(`${TRANSLATION_KEY}.nationality`)}
          testId="normalGatepass-nationality"
          disabled
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "passportNumberExpirationDate">
          name="passportNumberExpirationDate"
          type="text"
          label={t(`${TRANSLATION_KEY}.passport-number-expiration-date`)}
          testId="normalGatepass-passportNumberExpirationDate"
          disabled
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "phoneNumber">
          required
          name="phoneNumber"
          type="text"
          label={t(`${TRANSLATION_KEY}.phone-number`)}
          testId="normalGatepass-phoneNumber"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "sponsorCompany">
          required
          name="sponsorCompany"
          type="autocomplete"
          options={sponsorCompanies}
          label={t(`${TRANSLATION_KEY}.sponsor-company`)}
          testId="normalGatepass-sponsorCompany"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "expectedEntryDate">
          required
          name="expectedEntryDate"
          type="date-picker"
          label={t(`${TRANSLATION_KEY}.expected-entry-date`)}
          testId="normalGatepass-expectedEntryDate"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
          dateConstraint="not-past"
          timePicker={false}
        />

        <FormItem<NormalGatepassFormData, "port">
          required
          name="port"
          type="autocomplete"
          options={formattedPortOptions}
          label={t(`${TRANSLATION_KEY}.port`)}
          testId="normalGatepass-port"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "gate">
          required
          name="gate"
          type="autocomplete"
          options={sponsorGates}
          label={t(`${TRANSLATION_KEY}.gate-selection`)}
          testId="normalGatepass-gate"
          disabled={isGateDisabled}
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "visitorCompany">
          required
          name="visitorCompany"
          type="text"
          label={t(`${TRANSLATION_KEY}.visitor-company-name-en`)}
          testId="normalGatepass-visitorCompany"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "visitorCompanyArabic">
          required
          name="visitorCompanyArabic"
          type="text"
          dir="rtl"
          label={t(`${TRANSLATION_KEY}.visitor-company-name-ar`)}
          testId="normalGatepass-visitorCompanyArabic"
          actionInLabel
          textForActionInLabel={t("security-clearance.translate")}
          onActionClicked={() =>
            redirectForTranslateFromEnglishToArabic(visitorCompanyEnglish ?? "")
          }
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
        <FormItem<NormalGatepassFormData, "reference">
          name="reference"
          type="text"
          label={t(`${TRANSLATION_KEY}.reference`)}
          testId="normalGatepass-reference"
          onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
        />
      </FormSection>
    </FormProvider>
  );
};

export default VisitorDetails;
